import { Formik, Form } from 'formik'
import moment from 'moment'
import { useState, useContext } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { colors } from '@/styles'
import { Button } from '@/components/button'
import { showSignatureModal } from '@/components/signature-modal'
import SignaturePreview from '@/components/signature-preview'
import { show as showSuccessModal } from '@/components/success-modal'
import config from '@/config'
import useProfile from '@/hooks/use-profile'
import Page from '@/components/page'
import { Input } from '@/components/form'
import Panel from '@/components/panel'
import useHasScrolledToBottom from '@/hooks/use-has-scrolled-to-bottom'
import {
  StyledContentWrapper,
  StyledPanel,
  StyledAreaTitle,
  StyledButtonBar,
  DataRow,
  WarningRow,
  CheckboxText,
  StyledAnchor,
  DirectDepositPageTitle
} from './elements'
import Context from './context'
import AgreementText from './rent-collection-services-agreement'

const { WEB_URL } = config

const TermsWrapper = styled.div`
  ${props =>
    props.isModal &&
    `
    overflow: hidden;
    border: 1px solid ${colors.grey88};
    border-radius: 6px;
    margin-bottom: 24px;
  `}
`

const TermsScrollContainer = styled.div`
  ${props =>
    props.isModal &&
    `
    max-height: 300px;
    padding: 24px;
    overflow-y: auto;
  `}
`

const AgreementScrollContainer = styled.div`
  ${props =>
    props.isModal &&
    `
    max-height: 300px;
    padding: 24px;
    overflow-y: auto;
    border: 1px solid #CCCCCC;
    border-radius: 8px;
  `}
`

const CheckBox = styled(Input)`
  margin: 0 0 20px 0;
`

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;

  ${props =>
    !props.isModal &&
    `
    padding-top: 24px;
  `}

  ${props =>
    props.top &&
    `
    padding-top: 16px;
  `};

  ${CheckBox} {
    margin-left: 20px;
  }
`

const SignatureWrapper = styled.div`
  max-width: 325px;
  margin: 0 auto;
`

const Review = ({ handleBack, onSuccess, isModal }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const userProfile = useProfile()
  const { formData } = useContext(Context)
  const [showError, setShowError] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [acceptedTerms2, setAcceptedTerms2] = useState(false)
  const [acceptedTerms3, setAcceptedTerms3] = useState(false)
  const [acceptedTerms4, setAcceptedTerms4] = useState(false)
  const { hasScrolledToBottom, scrollRef } = useHasScrolledToBottom()

  const initialValues = {
    ...formData,
    user_signature: null
  }

  const onSubmit = async (values, { setSubmitting }) => {
    const response = await dispatch.settlements.updateUnionPayPayout(values)
    setSubmitting(false)
    if (response.ok) {
      showSuccessModal({
        title: t('l.payment_settings.view.you_are_all_set.text', `You're all set!`),
        message: t(
          'l.payment_settings.view.payout_deposit_submit_successfully.text',
          'Congratulations! You have successfully submitted your payout deposit information.'
        ),
        buttonText: t('common.done', 'Done')
      })
      onSuccess()
    }
  }

  const isInComplete =
    !acceptedTerms || !acceptedTerms2 || !acceptedTerms3 || !acceptedTerms4 || !hasScrolledToBottom

  return (
    <>
      {!isModal && <DirectDepositPageTitle />}
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        {({ values, setFieldValue, isSubmitting }) => {
          const handleSaveSignature = ({ signature }) => {
            setFieldValue('user_signature', signature)
          }
          const hasSigned = !!values.user_signature

          return (
            <Form className="flex-full">
              <Page>
                <StyledContentWrapper isModal={isModal}>
                  <StyledAreaTitle isModal={isModal}>
                    {t(
                      'l.payment_settings.view.review_rent_collection.subtitle',
                      'Review Rent Collection Services Agreement'
                    )}
                  </StyledAreaTitle>
                  <StyledPanel isModal={isModal}>
                    <TermsWrapper isModal={isModal}>
                      <TermsScrollContainer isModal={isModal}>
                        <DataRow>
                          <p>
                            {t(
                              'l.payment_settings.view.date_of_agreement.label',
                              'Date of agreement'
                            )}
                            :
                          </p>
                          <p>
                            <strong>{moment().format('MM-DD-YYYY')}</strong>
                          </p>
                        </DataRow>
                        <WarningRow>
                          {t(
                            'l.payment_settings.view.add_payout_agreement_warning.text',
                            'By signing below, you acknowledge and agree to the following:'
                          )}
                        </WarningRow>
                        <AgreementScrollContainer ref={scrollRef} isModal={!isModal}>
                          <Panel hSpace={0} vSpace={0}>
                            <AgreementText />
                          </Panel>
                        </AgreementScrollContainer>
                        {showError && !hasScrolledToBottom && (
                          <WarningRow marginTop={0} marginBottom={0}>
                            {t(
                              'b.payments.view.required_scroll_bottom.text',
                              'Required: You must scroll to the bottom of the agreement before you can sign.'
                            )}
                          </WarningRow>
                        )}
                      </TermsScrollContainer>
                    </TermsWrapper>
                    <CheckboxContainer isModal={isModal}>
                      <CheckboxText htmlFor="agree_to_terms">
                        {t(
                          'l.payment_settings.view.collect_services_agreement.label',
                          'I have read the above Rent Collection Services Agreement and understand that by signing below I agree to be bound by the Collection Services Agreement.'
                        )}
                      </CheckboxText>
                      <CheckBox
                        data-cy="agree_to_terms"
                        id="agree_to_terms"
                        data-testid="agree-to-terms"
                        type="checkbox"
                        checked={acceptedTerms}
                        disabled={!hasScrolledToBottom}
                        onChange={e => setAcceptedTerms(e.target.checked)}
                      />
                    </CheckboxContainer>
                    {showError && !acceptedTerms && (
                      <WarningRow marginTop={0} marginBottom={0}>
                        {t('common.required', 'Required')}
                      </WarningRow>
                    )}
                    <CheckboxContainer isModal={isModal} top>
                      <CheckboxText htmlFor="agree_to_terms_2">
                        {t(
                          'l.payment_settings.view.prompton_agree.label',
                          'I understand that the rent collection service is provided by Prompton Real Estate Services Inc.'
                        )}
                      </CheckboxText>
                      <CheckBox
                        data-cy="agree_to_terms_2"
                        id="agree_to_terms_2"
                        data-testid="agree-to-terms_2"
                        type="checkbox"
                        checked={acceptedTerms2}
                        disabled={!hasScrolledToBottom}
                        onChange={e => setAcceptedTerms2(e.target.checked)}
                      />
                    </CheckboxContainer>
                    {showError && !acceptedTerms2 && (
                      <WarningRow marginTop={0} marginBottom={0}>
                        {t('common.required', 'Required')}
                      </WarningRow>
                    )}
                    <CheckboxContainer isModal={isModal} top>
                      <CheckboxText htmlFor="agree_to_terms_3">
                        {t(
                          'l.payment_settings.view.tax_resident_agree.label',
                          'I confirm that I am a tax resident of Canada and that I will immediately cancel my receipt of the rent collection services if my tax residency changes.'
                        )}
                      </CheckboxText>
                      <CheckBox
                        data-cy="agree_to_terms_3"
                        id="agree_to_terms_3"
                        data-testid="agree-to-terms_3"
                        type="checkbox"
                        checked={acceptedTerms3}
                        disabled={!hasScrolledToBottom}
                        onChange={e => setAcceptedTerms3(e.target.checked)}
                      />
                    </CheckboxContainer>
                    {showError && !acceptedTerms3 && (
                      <WarningRow marginTop={0} marginBottom={0}>
                        {t('common.required', 'Required')}
                      </WarningRow>
                    )}
                    <CheckboxContainer isModal top>
                      <CheckboxText htmlFor="agree_to_terms_4">
                        <Trans i18nKey="l.payment_settings.view.payee_terms_conditions_1.label">
                          I have read and agree to liv.rent's{' '}
                          <StyledAnchor
                            as="a"
                            href={`${WEB_URL}/terms`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Terms & Conditions
                          </StyledAnchor>{' '}
                          &{' '}
                          <StyledAnchor
                            as="a"
                            href={`${WEB_URL}/privacy`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Privacy Policy
                          </StyledAnchor>
                          .
                        </Trans>
                      </CheckboxText>
                      <CheckBox
                        data-cy="agree_to_terms_4"
                        id="agree_to_terms_4"
                        data-testid="agree-to-terms_4"
                        type="checkbox"
                        checked={acceptedTerms4}
                        disabled={!hasScrolledToBottom}
                        onChange={e => setAcceptedTerms4(e.target.checked)}
                      />
                    </CheckboxContainer>
                    {showError && !acceptedTerms4 && (
                      <WarningRow marginTop={0} marginBottom={0}>
                        {t('common.required', 'Required')}
                      </WarningRow>
                    )}
                    <SignatureWrapper>
                      <SignaturePreview
                        key={userProfile.id}
                        isCurrentUser
                        toggleSignaturePad={() => {
                          if (isInComplete) {
                            setShowError(true)
                          }
                          showSignatureModal({ onSave: handleSaveSignature })
                        }}
                        signatures={hasSigned ? [values.user_signature] : []}
                        renter={userProfile}
                        localSignature={values.user_signature}
                      />
                    </SignatureWrapper>
                  </StyledPanel>
                </StyledContentWrapper>
              </Page>
              <StyledButtonBar isModal={isModal} sticky={!isModal}>
                <Button theme="secondary" size={isModal ? 'medium' : 'small'} onClick={handleBack}>
                  {t('common.back', 'Back')}
                </Button>
                <Button
                  theme="primary"
                  size={isModal ? 'medium' : 'small'}
                  type="submit"
                  disabled={isInComplete || !hasSigned}
                  isSubmitting={isSubmitting}
                >
                  {t('common.submit', 'Submit')}
                </Button>
              </StyledButtonBar>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default Review
